import Component from "@base/js/component";

export default class Drawer extends Component {
  // Defines the element selector which will initialize this component
  static selector = ".drawer";

  // Defines children that needs to be queried as part of this component
  static children = {
    header: ".drawer__header",
    body: ".drawer__content",
  };

  // Defines event handlers to attach to children
  eventHandlers = {
    header: {
      click: this.onHeaderClick,
    },
    body: {
      transitionstart: this.onBodyTransitionStart,
      transitionend: this.onBodyTransitionEnd,
    },
  };

  isTransitioning = false;

  callbacks = {
    beforeExpand: [],
  };

  get isExpanded() {
    return this.element.classList.contains("drawer--expanded");
  }

  // Will be called upon component instantiation
  afterInit() {
    const { toggleSelector } = this.element.dataset;
    const toggleElement = toggleSelector
      ? this.element.querySelector(toggleSelector)
      : null;

    const { header, body } = this.children;

    if (toggleElement) {
      toggleElement.addEventListener("click", this.onHeaderClick.bind(this));
    }

    if (header && body) {
      const slugText = header.innerText
        .toLowerCase()
        .replace(/ /g, "-")
        .replace(/[^\w-]+/g, "");
      const slugID = Math.random().toString(36).substr(2, 5);
      const slugSuffix = slugText || slugID;
      header.setAttribute("aria-controls", `controls__${slugSuffix}`);
      header.setAttribute("id", `label__${slugSuffix}`);
      body.setAttribute("id", `controls__${slugSuffix}`);
      body.setAttribute("aria-labelledby", `label__${slugSuffix}`);
      body.setAttribute("aria-hidden", "true");

      header.addEventListener("keydown", this.onHeaderKeyDown.bind(this));

      const multiSelect = body.querySelector(".multi-select__selections");
      if (multiSelect) {
        const checkboxes = multiSelect.querySelectorAll(".checkbox");

        checkboxes.forEach((el) => {
          el.setAttribute("role", "checkbox");
          const checkInput = el.querySelector("input");
          el.addEventListener("click", this.onCheckboxClick.bind(this));
          if (checkInput) {
            el.setAttribute(
              "aria-checked",
              checkInput.checked ? "true" : "false"
            );
          }
        });

        multiSelect.setAttribute("role", "group");
        multiSelect.setAttribute("aria-labelledby", `label__${slugSuffix}`);
        multiSelect.setAttribute("tabindex", "-1");
        multiSelect.addEventListener(
          "keydown",
          this.onMultiSelectKeyUp.bind(this)
        );
      }

      const multiSelectSearch = body.querySelector(
        ".multi-select__selections-search"
      );
      if (multiSelectSearch) {
        const input = multiSelectSearch.querySelector("input");
        const button = multiSelectSearch.querySelector("button");
        if (input && button) {
          input.setAttribute("tabindex", "-1");
          input.setAttribute("aria-hidden", "true");
          button.setAttribute("tabindex", "-1");
          button.setAttribute("aria-hidden", "true");
        }
      }
    }
  }

  onMultiSelectKeyUp(e) {
    const itm = e.target;
    const prev = itm.previousElementSibling;
    const next = itm.nextElementSibling;

    if (e.which === 38 && prev) {
      // On arrow up move focus to the previous item.
      itm.setAttribute("tabindex", "-1");
      prev.setAttribute("tabindex", "0");
      prev.focus();
    } else if (e.which === 40 && next) {
      // On arrow down move focus to the next item.
      itm.setAttribute("tabindex", "-1");
      next.setAttribute("tabindex", "0");
      next.focus();
    } else if (e.which === 27 && this.children.header) {
      // click header when user press esc, toggle expanded
      this.children.header.focus();
      this.children.header.click();
    } else if (/^(13|32)$/.test(e.which)) {
      e.preventDefault();
      const lbl = itm.querySelector("label");
      const inpt = itm.querySelector("input");
      if (lbl && inpt) {
        lbl.click();
        itm.setAttribute("aria-checked", inpt.checked ? "true" : "false");
      }
    }
  }

  onHeaderClick(e) {
    e.preventDefault();

    if (!this.isTransitioning) {
      this.toggleExpanded();
    }
  }

  onHeaderKeyDown(e) {
    if (/^(13|32)$/.test(e.which)) {
      e.preventDefault();
      this.children.header.click();
    }
  }

  onCheckboxClick(e) {
    const { target } = e;
    const targetTagName = target.tagName.toString().toLowerCase();
    if (targetTagName === "div") {
      e.preventDefault();
      const lbl = target.querySelector("label");
      const inpt = target.querySelector("input");
      if (lbl && inpt) {
        lbl.click();
        target.setAttribute("aria-checked", inpt.checked ? "true" : "false");
        target.focus();
      }
    }
    if (targetTagName === "input") {
      const checkboxLabel = e.target;
      const checkboxParent = checkboxLabel.parentElement.parentElement;
      const lbl = checkboxParent.querySelector("label");
      const inpt = checkboxParent.querySelector("input");
      if (lbl && inpt) {
        checkboxParent.setAttribute(
          "aria-checked",
          inpt.checked ? "true" : "false"
        );
        checkboxParent.focus();
      }
    }
  }

  onBodyTransitionStart(e) {
    if (e.target === e.currentTarget) {
      this.isTransitioning = true;
    }
  }

  onBodyTransitionEnd(e) {
    if (e.target === e.currentTarget) {
      this.children.body.style.height = "";
      this.element.classList.toggle("drawer--expanded", !this.isExpanded);
      this.isTransitioning = false;
      this.children.body.style.zIndex = ``;
    }
  }

  toggleExpanded(willExpand = null) {
    /* eslint-disable */
    const drawerWillExpand =
      typeof willExpand === "boolean" ? willExpand : !this.isExpanded;
    const drawerBodyHeight = this.children.body.scrollHeight;

    const multiSelect = this.children.body.querySelector('.multi-select__selections');

    const multiSelectSearch = this.children.body.querySelector(".multi-select__selections-search");
    // Expand
    if (drawerWillExpand) {
      this.callbacks.beforeExpand.forEach((callback) => callback && callback());
      this.children.body.style.height = "0px";
      this.children.header.setAttribute('aria-expanded', 'true');
      this.children.body.setAttribute('aria-hidden', 'false');

      if (multiSelect) {
        const checkboxes = multiSelect.querySelectorAll('.checkbox');
        if(checkboxes.length > 0) {
          checkboxes[0].setAttribute('tabindex','0')
        }
      }

      if(multiSelectSearch) {
        const input =  multiSelectSearch.querySelector('input');
        const button =  multiSelectSearch.querySelector('button');
        if(input && button) {
          input.setAttribute('tabindex', '0');
          input.setAttribute('aria-hidden', 'false');
          button.setAttribute('tabindex', '0');
          button.setAttribute('aria-hidden', 'false');
        }
      }

      // Forces layout so out height animation will work
      window.pageYOffset; // eslint-disable-line

      this.children.body.style.height = `${drawerBodyHeight}px`;

      if (this.element.parentElement.classList.contains(`multi-select--calendar`) &&
        (this.element.querySelector(`[name="date_range"]:checked`) &&
        this.element.querySelector(`[name="date_range"]:checked`).value === 'date-specific-date'
        )
      ) {
          this.element.querySelector(`.calendar`).style.display = "block";
      }
    }
    // Collapse
    else {
      this.children.body.style.zIndex = 999;
      this.children.body.style.height = `${drawerBodyHeight}px`;
      this.children.header.setAttribute('aria-expanded', 'false');
      this.children.body.setAttribute('aria-hidden', 'true');

      if (multiSelect) {
        const checkboxes = multiSelect.querySelectorAll('.checkbox');
        checkboxes.forEach((el) => {
          el.removeAttribute('tabindex');
        });
      }

      if(multiSelectSearch) {
        const input =  multiSelectSearch.querySelector('input');
        const button =  multiSelectSearch.querySelector('button');
        if(input && button) {
          input.setAttribute('tabindex', '-1');
          input.setAttribute('aria-hidden', 'true');
          button.setAttribute('tabindex', '-1');
          button.setAttribute('aria-hidden', 'true');
        }
      }

      // Forces to a different stack so height animation will work.
      setTimeout(() => {
        this.children.body.style.height = "0px";
        if (this.element.parentElement.classList.contains(`multi-select--calendar`)) {
          this.element.querySelector(`.calendar`).style.display = "none";
        }
      }, 100);
    }
  }

  // Adds beforeExpand callback
  beforeExpand(callback) {
    if (typeof callback === "function") {
      this.callbacks.beforeExpand.push(callback);
    }
  }
}

// Remove this line if this component is not intended
// to initialize on DOM load.
// Drawer.init();
